// ScreenshotWithCallouts — annotated bl181 screenshot with pinned labels.
//
// Percent-based pin coordinates so the same YAML works on retina +
// non-retina captures (spec §component-tree). Pins are rendered
// absolutely over the image; each pin has a visible numbered chip and
// an accessible text label pinned via aria-describedby so screen readers
// read the callout body alongside the image.

import type { Screenshot } from './types'

interface ScreenshotWithCalloutsProps {
  screenshot: Screenshot
  stepIndex: number
}

export function ScreenshotWithCallouts({
  screenshot,
  stepIndex,
}: ScreenshotWithCalloutsProps) {
  const { src, alt, placeholder, callouts = [] } = screenshot
  const describeId = `help-screenshot-desc-${stepIndex}`
  return (
    <figure
      className="relative overflow-hidden rounded-xl border border-hairline bg-surface-raised shadow-sm dark:border-slate-700 dark:bg-slate-900"
      data-testid={`help-screenshot-${stepIndex}`}
    >
      <div className="relative">
        <img
          src={src}
          alt={alt}
          aria-describedby={callouts.length > 0 ? describeId : undefined}
          loading="lazy"
          className="block h-auto w-full"
        />
        {callouts.map((pin, i) => (
          <span
            key={`${pin.label}-${i}`}
            role="note"
            aria-label={`Callout ${i + 1}: ${pin.label}`}
            className="absolute z-10 -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${pin.x_pct}%`, top: `${pin.y_pct}%` }}
          >
            <span className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-butler-500 text-[11px] font-bold text-white shadow-md ring-1 ring-butler-500/40">
              {i + 1}
            </span>
          </span>
        ))}
      </div>
      {(callouts.length > 0 || placeholder) && (
        <figcaption
          id={describeId}
          className="border-t border-hairline bg-surface-page px-4 py-2 text-xs text-ink-soft dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400"
        >
          {placeholder && (
            <span className="mr-3 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-500/20 dark:text-amber-200">
              Placeholder screenshot
            </span>
          )}
          {callouts.length > 0 && (
            <ol className="mt-1 list-inside list-decimal space-y-0.5">
              {callouts.map((pin, i) => (
                <li key={`${pin.label}-cap-${i}`}>{pin.label}</li>
              ))}
            </ol>
          )}
        </figcaption>
      )}
    </figure>
  )
}
